/**
 * EOS Issues (IDS) - Horizontal Slider by Person (Slack-style)
 * 
 * Features:
 * - Horizontal scrollable columns by user
 * - Card-based display with status/priority badges
 * - Modal-based add/edit/IDS workflow (no side panels)
 * - Inline "+ Add Issue" per person column
 * - Tabs filter by status (All, Open, In Discussion, Solved)
 * - Drag-and-drop reordering within user columns
 * - IDS Process: Identify → Discuss → Solve
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AlertCircle, 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Clock,
  MessageSquare,
  CheckCircle2,
  Circle,
  GripVertical,
  Lightbulb,
  Users,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';
import { format } from 'date-fns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  identified: { 
    label: 'Open', 
    color: 'bg-orange-500', 
    icon: Circle, 
    textColor: 'text-orange-500',
    bgLight: 'bg-orange-100 dark:bg-orange-950/30',
    borderColor: 'border-orange-400'
  },
  in_discussion: { 
    label: 'Discussing', 
    color: 'bg-blue-500', 
    icon: MessageSquare, 
    textColor: 'text-blue-500',
    bgLight: 'bg-blue-100 dark:bg-blue-950/30',
    borderColor: 'border-blue-400'
  },
  solved: { 
    label: 'Solved', 
    color: 'bg-green-500', 
    icon: CheckCircle2, 
    textColor: 'text-green-500',
    bgLight: 'bg-green-100 dark:bg-green-950/30',
    borderColor: 'border-green-400'
  },
  archived: { 
    label: 'Archived', 
    color: 'bg-slate-500', 
    icon: Clock, 
    textColor: 'text-slate-400',
    bgLight: 'bg-slate-100 dark:bg-slate-800/50',
    borderColor: 'border-slate-400'
  },
};

// Priority configuration
const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-500', emoji: '🔴' },
  medium: { label: 'Medium', color: 'bg-yellow-500', emoji: '🟡' },
  low: { label: 'Low', color: 'bg-green-500', emoji: '🟢' },
};

// Issue Card Content Component (Base)
const IssueCardContent = ({ issue, onClick, isDragging }) => {
  const status = STATUS_CONFIG[issue.status] || STATUS_CONFIG.identified;
  const priority = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG.medium;
  const StatusIcon = status.icon;
  
  const calculateAge = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  return (
    <Card 
      className={`dark:bg-slate-700/50 border-l-4 ${status.borderColor} hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg group ${isDragging ? 'shadow-xl ring-2 ring-blue-400/50' : ''}`}
      onClick={() => !isDragging && onClick(issue)}
      data-testid={`issue-card-${issue.id}`}
    >
      <CardContent className="p-3">
        {/* Status & Priority Badges */}
        <div className="flex items-center justify-between mb-2">
          <Badge 
            variant="outline" 
            className={`${status.color} text-white border-0 text-xs`}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {status.label}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {priority.emoji} {priority.label}
          </Badge>
        </div>
        
        {/* Title */}
        <p className="text-sm font-medium dark:text-white mb-2 line-clamp-2">
          {issue.title}
        </p>
        
        {/* Description preview */}
        {issue.description && (
          <p className="text-xs text-slate-400 mb-2 line-clamp-1">
            {issue.description}
          </p>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-600/50 text-xs text-slate-400">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {calculateAge(issue.created_at)}d old
          </span>
          {issue.department && (
            <Badge variant="outline" className="text-[10px] py-0">{issue.department}</Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Sortable Issue Card Component with drag handle
const SortableIssueCard = ({ issue, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: issue.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group/card">
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute left-1 top-1/2 -translate-y-1/2 z-10 cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 opacity-0 group-hover/card:opacity-100 transition-opacity bg-slate-800/80 rounded p-1"
        onClick={(e) => e.stopPropagation()}
        data-testid={`drag-handle-${issue.id}`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <IssueCardContent issue={issue} onClick={onClick} isDragging={isDragging} />
    </div>
  );
};

// Person Column Component with Sortable Context
const PersonColumn = ({ user, issues, onAddIssue, onIssueClick }) => {
  const userIssues = issues.filter(i => i.owner_id === user.id);
  const issueIds = userIssues.map(i => i.id);
  
  // Count by status
  const openCount = userIssues.filter(i => i.status === 'identified').length;
  const discussingCount = userIssues.filter(i => i.status === 'in_discussion').length;
  const solvedCount = userIssues.filter(i => i.status === 'solved').length;
  
  return (
    <div className="flex-shrink-0 w-72 bg-slate-800/40 rounded-lg border border-slate-700" data-testid={`person-column-${user.id}`}>
      {/* Column Header */}
      <div className="p-3 border-b border-slate-700 sticky top-0 bg-slate-800/90 backdrop-blur-sm rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserAvatar user={user} size="sm" />
            <div>
              <p className="font-medium text-sm dark:text-white">
                {user.first_name} {user.last_name}
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="text-orange-400">{openCount} open</span>
                {discussingCount > 0 && <span className="text-blue-400">• {discussingCount} disc.</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Issues List with Sortable Context */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
        <SortableContext items={issueIds} strategy={verticalListSortingStrategy}>
          {userIssues.map(issue => (
            <SortableIssueCard 
              key={issue.id} 
              issue={issue} 
              onClick={onIssueClick}
            />
          ))}
        </SortableContext>
        
        {userIssues.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-4">No issues assigned</p>
        )}
        
        {/* Add Issue Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50 border border-dashed border-slate-600"
          onClick={() => onAddIssue(user)}
          data-testid={`add-issue-btn-${user.id}`}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Issue
        </Button>
      </div>
    </div>
  );
};

const EOSIssuesSlider = () => {
  const [issues, setIssues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const scrollContainerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  
  // Drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Modal States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [idsDialogOpen, setIdsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [prefilledUser, setPrefilledUser] = useState(null);
  
  // IDS Step state
  const [idsStep, setIdsStep] = useState('identify');
  
  // Form State
  const [issueForm, setIssueForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    owner_id: '',
    department: ''
  });
  
  // IDS Data State
  const [idsData, setIdsData] = useState({
    root_cause: '',
    discussion_notes: '',
    solution: ''
  });

  useEffect(() => {
    fetchIssues();
    fetchUsers();
  }, []);

  const fetchIssues = async () => {
    try {
      const response = await axios.get(`${API}/eos/issues`);
      setIssues(response.data);
    } catch (error) {
      console.error('Error fetching issues:', error);
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      const activeUsers = response.data.filter(u => u.is_active !== false);
      setUsers(activeUsers.slice(0, 15));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddIssue = (user) => {
    setPrefilledUser(user);
    setIssueForm({
      title: '',
      description: '',
      priority: 'medium',
      owner_id: user?.id || user?.user_id || '',
      department: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCreateIssue = async () => {
    try {
      if (!issueForm.title.trim()) {
        toast.error('Please enter a title');
        return;
      }
      if (!issueForm.owner_id) {
        toast.error('Please select an owner');
        return;
      }

      await axios.post(`${API}/eos/issues`, issueForm);
      toast.success('Issue created successfully!');
      setCreateDialogOpen(false);
      resetIssueForm();
      fetchIssues();
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error(error.response?.data?.detail || 'Failed to create issue');
    }
  };

  const handleIssueClick = (issue) => {
    setSelectedIssue(issue);
    
    // Determine current IDS step based on status
    if (issue.status === 'solved') {
      setIdsStep('solve');
    } else if (issue.status === 'in_discussion') {
      setIdsStep('discuss');
    } else {
      setIdsStep('identify');
    }
    
    // Load existing IDS data
    setIdsData({
      root_cause: issue.ids_process?.identify?.root_cause || '',
      discussion_notes: issue.ids_process?.discuss?.discussion_notes || '',
      solution: issue.ids_process?.solve?.solution || ''
    });
    
    setIdsDialogOpen(true);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedIssue) return;
    
    try {
      await axios.put(`${API}/eos/issues/${selectedIssue.id}`, { status: newStatus });
      toast.success(`Issue marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchIssues();
      
      // Update selected issue
      setSelectedIssue(prev => prev ? { ...prev, status: newStatus } : null);
      
      // Move to next step if not solved
      if (newStatus === 'in_discussion') {
        setIdsStep('discuss');
      } else if (newStatus === 'solved') {
        setIdsStep('solve');
        setIdsDialogOpen(false);
      }
    } catch (error) {
      console.error('Error updating issue:', error);
      toast.error('Failed to update issue');
    }
  };

  const handleSolveIssue = async () => {
    if (!selectedIssue) return;
    
    if (!idsData.solution.trim()) {
      toast.error('Please document the solution');
      return;
    }
    
    try {
      await axios.put(`${API}/eos/issues/${selectedIssue.id}`, { status: 'solved' });
      toast.success('Issue solved successfully!');
      setIdsDialogOpen(false);
      setSelectedIssue(null);
      fetchIssues();
    } catch (error) {
      console.error('Error solving issue:', error);
      toast.error('Failed to solve issue');
    }
  };

  const handleDeleteIssue = async () => {
    if (!selectedIssue) return;
    
    try {
      await axios.delete(`${API}/eos/issues/${selectedIssue.id}`);
      toast.success('Issue deleted');
      setDeleteDialogOpen(false);
      setIdsDialogOpen(false);
      setSelectedIssue(null);
      fetchIssues();
    } catch (error) {
      console.error('Error deleting issue:', error);
      toast.error('Failed to delete issue');
    }
  };

  const resetIssueForm = () => {
    setIssueForm({
      title: '',
      description: '',
      priority: 'medium',
      owner_id: '',
      department: ''
    });
    setPrefilledUser(null);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (!over || active.id === over.id) return;

    const activeIssue = issues.find(i => i.id === active.id);
    const overIssue = issues.find(i => i.id === over.id);
    
    if (!activeIssue || !overIssue) return;
    
    // Only allow reordering within the same user's column
    if (activeIssue.owner_id !== overIssue.owner_id) {
      toast.info('Issues can only be reordered within the same person\'s column');
      return;
    }

    const userIssues = issues.filter(i => i.owner_id === activeIssue.owner_id);
    const otherIssues = issues.filter(i => i.owner_id !== activeIssue.owner_id);
    
    const oldIndex = userIssues.findIndex(i => i.id === active.id);
    const newIndex = userIssues.findIndex(i => i.id === over.id);
    
    const reorderedUserIssues = arrayMove(userIssues, oldIndex, newIndex);
    
    setIssues([...otherIssues, ...reorderedUserIssues]);
    toast.success('Issue order updated');
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeIssue = activeId ? issues.find(i => i.id === activeId) : null;

  // Filter issues based on status
  const filteredIssues = statusFilter === 'all' 
    ? issues 
    : issues.filter(i => i.status === statusFilter);

  // Calculate stats
  const stats = {
    total: issues.length,
    open: issues.filter(i => i.status === 'identified').length,
    discussing: issues.filter(i => i.status === 'in_discussion').length,
    solved: issues.filter(i => i.status === 'solved').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Issues...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="issues-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <div>
              <h1 className="text-2xl font-bold dark:text-white">Issues (IDS)</h1>
              <p className="text-sm text-muted-foreground">
                Identify, Discuss, and Solve problems systematically
              </p>
            </div>
          </div>
          
          <Button onClick={() => handleAddIssue(null)} data-testid="add-issue-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Issue
          </Button>
        </div>

        {/* Status Filter Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
          <TabsList className="bg-slate-800/40">
            <TabsTrigger value="all" data-testid="tab-all">All ({stats.total})</TabsTrigger>
            <TabsTrigger value="identified" data-testid="tab-open">
              <Circle className="h-3 w-3 mr-1 text-orange-500" />
              Open ({stats.open})
            </TabsTrigger>
            <TabsTrigger value="in_discussion" data-testid="tab-discussing">
              <MessageSquare className="h-3 w-3 mr-1 text-blue-500" />
              Discussing ({stats.discussing})
            </TabsTrigger>
            <TabsTrigger value="solved" data-testid="tab-solved">
              <CheckCircle2 className="h-3 w-3 mr-1 text-green-500" />
              Solved ({stats.solved})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Stats Bar */}
        <div className="flex gap-4 p-3 bg-slate-800/40 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Total:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{filteredIssues.length}</Badge>
          </div>
          {Object.entries(STATUS_CONFIG).slice(0, 3).map(([key, config]) => {
            const count = filteredIssues.filter(i => i.status === key).length;
            if (count === 0 && statusFilter !== 'all' && statusFilter !== key) return null;
            const Icon = config.icon;
            return (
              <div key={key} className="flex items-center gap-2">
                <Icon className={`h-4 w-4 ${config.textColor}`} />
                <Badge className={config.color}>{count}</Badge>
              </div>
            );
          })}
        </div>

        {/* Horizontal Slider Container with Drag and Drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="relative">
            {/* Scroll Buttons */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollLeft}
              data-testid="scroll-left-btn"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
              onClick={scrollRight}
              data-testid="scroll-right-btn"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
            
            {/* Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="flex gap-4 overflow-x-auto px-10 pb-4 scrollbar-thin"
              style={{ scrollbarWidth: 'thin' }}
              data-testid="issues-slider"
            >
              {users.map(user => (
                <PersonColumn
                  key={user.id}
                  user={user}
                  issues={filteredIssues}
                  onAddIssue={handleAddIssue}
                  onIssueClick={handleIssueClick}
                />
              ))}
              
              {users.length === 0 && (
                <div className="flex-1 flex items-center justify-center p-8 text-slate-500">
                  No team members found.
                </div>
              )}
            </div>
          </div>
          
          {/* Drag Overlay */}
          <DragOverlay>
            {activeIssue && (
              <div className="w-72 opacity-90">
                <IssueCardContent 
                  issue={activeIssue} 
                  onClick={() => {}} 
                  isDragging={true}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>

        {/* Empty State for filtered view */}
        {filteredIssues.length === 0 && statusFilter !== 'all' && (
          <div className="text-center py-8 text-slate-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No {STATUS_CONFIG[statusFilter]?.label} issues.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => handleAddIssue(null)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create an issue
            </Button>
          </div>
        )}
      </div>

      {/* Create Issue Modal */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Create New Issue
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {prefilledUser && (
                <span>Assigning to {prefilledUser.first_name} {prefilledUser.last_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Issue Title *</Label>
              <Input
                value={issueForm.title}
                onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                placeholder="e.g., Sales pipeline conversion dropping"
                className="dark:bg-slate-700 dark:border-slate-600"
                data-testid="issue-title-input"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Description</Label>
              <Textarea
                value={issueForm.description}
                onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                placeholder="Provide more details about the issue..."
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-200">Priority</Label>
                <Select 
                  value={issueForm.priority} 
                  onValueChange={(val) => setIssueForm({ ...issueForm, priority: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="issue-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="high">🔴 High Priority</SelectItem>
                    <SelectItem value="medium">🟡 Medium Priority</SelectItem>
                    <SelectItem value="low">🟢 Low Priority</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Owner *</Label>
                <Select 
                  value={issueForm.owner_id} 
                  onValueChange={(val) => setIssueForm({ ...issueForm, owner_id: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600" data-testid="issue-owner-select">
                    <SelectValue placeholder="Select owner" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    {users.map(user => (
                      <SelectItem key={user.id || user.user_id} value={user.id || user.user_id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Department (Optional)</Label>
              <Select 
                value={issueForm.department} 
                onValueChange={(val) => setIssueForm({ ...issueForm, department: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="operations">Operations</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="general">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetIssueForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateIssue} data-testid="create-issue-btn">
              Create Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IDS Process Modal */}
      <Dialog open={idsDialogOpen} onOpenChange={setIdsDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              {selectedIssue?.title}
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400 flex items-center gap-2">
              {selectedIssue && (
                <>
                  <Badge className={PRIORITY_CONFIG[selectedIssue.priority]?.color}>
                    {PRIORITY_CONFIG[selectedIssue.priority]?.emoji} {PRIORITY_CONFIG[selectedIssue.priority]?.label}
                  </Badge>
                  <Badge className={STATUS_CONFIG[selectedIssue.status]?.color}>
                    {STATUS_CONFIG[selectedIssue.status]?.label}
                  </Badge>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-4 py-4">
              {/* IDS Steps Navigation */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <Button
                  variant={idsStep === 'identify' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setIdsStep('identify')}
                  className="gap-1"
                >
                  <Lightbulb className="h-4 w-4" />
                  1. Identify
                </Button>
                <div className="h-px w-6 bg-slate-600"></div>
                <Button
                  variant={idsStep === 'discuss' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setIdsStep('discuss')}
                  disabled={selectedIssue.status === 'identified'}
                  className="gap-1"
                >
                  <Users className="h-4 w-4" />
                  2. Discuss
                </Button>
                <div className="h-px w-6 bg-slate-600"></div>
                <Button
                  variant={idsStep === 'solve' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setIdsStep('solve')}
                  disabled={selectedIssue.status === 'identified'}
                  className="gap-1"
                >
                  <Target className="h-4 w-4" />
                  3. Solve
                </Button>
              </div>
              
              {/* Step Content */}
              {idsStep === 'identify' && (
                <div className="space-y-4">
                  <div className="border-l-4 border-orange-500 pl-4 py-2 bg-orange-950/20 rounded-r-lg">
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Identify the Root Cause</h3>
                    <p className="text-sm text-slate-400">
                      What&apos;s the REAL issue? Not the symptom, but the underlying problem.
                    </p>
                  </div>
                  
                  {selectedIssue.description && (
                    <div className="p-3 bg-slate-700/50 rounded-lg">
                      <Label className="text-xs text-slate-400">Description</Label>
                      <p className="text-sm dark:text-white mt-1">{selectedIssue.description}</p>
                    </div>
                  )}
                  
                  <div>
                    <Label className="dark:text-slate-200">Root Cause Analysis</Label>
                    <Textarea
                      placeholder="Document the root cause of this issue... Ask 'Why?' 5 times."
                      value={idsData.root_cause}
                      onChange={(e) => setIdsData({ ...idsData, root_cause: e.target.value })}
                      className="dark:bg-slate-700 dark:border-slate-600"
                      rows={4}
                    />
                  </div>
                  
                  <Button onClick={() => handleUpdateStatus('in_discussion')} className="w-full">
                    Start Discussion →
                  </Button>
                </div>
              )}
              
              {idsStep === 'discuss' && (
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-950/20 rounded-r-lg">
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Discuss Solutions</h3>
                    <p className="text-sm text-slate-400">
                      Collaborate with the team to explore potential solutions.
                    </p>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-200">Discussion Notes</Label>
                    <Textarea
                      placeholder="Document the discussion, ideas, and approaches explored..."
                      value={idsData.discussion_notes}
                      onChange={(e) => setIdsData({ ...idsData, discussion_notes: e.target.value })}
                      className="dark:bg-slate-700 dark:border-slate-600"
                      rows={5}
                    />
                  </div>
                  
                  <Button onClick={() => setIdsStep('solve')} className="w-full">
                    Proceed to Solve →
                  </Button>
                </div>
              )}
              
              {idsStep === 'solve' && (
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-950/20 rounded-r-lg">
                    <h3 className="font-semibold text-lg mb-1 dark:text-white">Document the Solution</h3>
                    <p className="text-sm text-slate-400">
                      What&apos;s the agreed-upon solution? What actions will be taken?
                    </p>
                  </div>
                  
                  <div>
                    <Label className="dark:text-slate-200">Solution *</Label>
                    <Textarea
                      placeholder="Document the solution and action plan..."
                      value={idsData.solution}
                      onChange={(e) => setIdsData({ ...idsData, solution: e.target.value })}
                      className="dark:bg-slate-700 dark:border-slate-600"
                      rows={5}
                    />
                  </div>
                  
                  <div className="p-3 bg-blue-950/30 rounded-lg border border-blue-800">
                    <p className="text-sm font-medium mb-2 dark:text-white">💡 Quick Action:</p>
                    <p className="text-xs text-slate-400 mb-2">
                      Create a To-Do directly from this issue to track action items.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        toast.info('Navigate to To-Dos page to create follow-up tasks');
                      }}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create To-Do from Issue
                    </Button>
                  </div>
                  
                  <Button onClick={handleSolveIssue} className="w-full bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Solved
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button variant="outline" onClick={() => { setIdsDialogOpen(false); setSelectedIssue(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Delete Issue?</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-slate-400">
              This action cannot be undone. This will permanently delete this issue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-slate-700 dark:border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteIssue}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EOSIssuesSlider;
